import { type ReactNode, useEffect, useMemo, useRef, useState } from "react"
import { GetShip } from "../../App/Context"
import {
  deps,
  many,
  nestedNoteRows,
  useDataStore,
  useDeps,
  useQueryReady,
  type DepsIds,
  type EntityRows,
} from "../../Data"
import type { IDay, ITrack, ITrip } from "../../Types"
import { SchemaEntities } from "../../Types/Generated/LogbookModel/Schema"
import { Subscription } from "../../Types/Generated/LogbookApi/Subscriptions"
import { TripDataContext } from "./DataContext"
import type { TripDataProps } from "./Params"
import { createStoreTripWriters } from "./writers"
import { useAuthoritativeSeedIds } from "../useAuthoritativeSeedIds"

const TripRecordDeps = deps(SchemaEntities.Cruise, {
  trips: many(SchemaEntities.Trip, { with: ["calculated", "calculatedV2", "notes"] }),
});

const TripDayDeps = deps(SchemaEntities.Trip, {
  days: many(SchemaEntities.Day, { with: ["calculated", "calculatedV2", "notes"] }),
});

const TripTrackDeps = deps(SchemaEntities.Trip, {
  tracks: many(SchemaEntities.Track),
});

function nestedRows(trip: ITrip, days: readonly IDay[], tracks: readonly ITrack[]): EntityRows {
  const notes = [...(trip.notes ?? []), ...days.flatMap((day) => day.notes ?? [])];
  return {
    Trip: [trip],
    Day: days,
    Track: tracks,
    ...nestedNoteRows(notes),
  };
}

interface TripDataInnerProps extends TripDataProps {
  children?: ReactNode;
  details: boolean;
  setDetails: (value: boolean) => void;
}

function TripDataInner({
  currentDays,
  initTracks,
  trip,
  tripChildren,
  details,
  setDetails,
  children,
  storeBacked,
}: TripDataInnerProps) {
  const store = useDataStore();
  const ship = GetShip();
  const tripIds: DepsIds<typeof TripRecordDeps.relationships> = { cruiseId: trip.cruiseId };
  const dayIds: DepsIds<typeof TripDayDeps.relationships> = { shipId: ship.id, tripId: trip.id };
  const trackIds: DepsIds<typeof TripTrackDeps.relationships> = { shipId: ship.id, tripId: trip.id };
  const { trips } = useDeps(TripRecordDeps, tripIds, { fetch: false });
  const { days } = useDeps(TripDayDeps, dayIds, {
    fetch: !tripChildren && currentDays === undefined,
  });
  const { tracks } = useDeps(TripTrackDeps, trackIds, {
    fetch: details && initTracks === undefined,
  });
  const writers = useMemo(
    () => createStoreTripWriters(store, { shipId: ship.id, tripId: trip.id }),
    [store, ship.id, trip.id],
  );
  const seededDayIds = useAuthoritativeSeedIds(
    currentDays,
    Subscription.Day,
    (day) => day.tripId === trip.id,
  );
  const seededTrackIds = useAuthoritativeSeedIds(
    initTracks,
    Subscription.Track,
    (track) => track.tripId === trip.id,
  );
  const daysReady = useQueryReady(
    store,
    SchemaEntities.Day,
    dayIds,
    !tripChildren && currentDays === undefined,
    currentDays !== undefined,
  );
  const tracksReady = useQueryReady(
    store,
    SchemaEntities.Track,
    trackIds,
    details && initTracks === undefined,
    initTracks !== undefined,
  );

  // `currentDays` is an authoritative initial slice (Focus/Current): do not widen
  // it with older rows already cached for this trip, but admit subsequent live Days
  // exactly as the legacy local array does.
  const resolvedTrip = (trips as ITrip[]).find((candidate) => candidate.id === trip.id) ?? trip;
  const selectedDays = storeBacked && currentDays
    ? (days as IDay[]).filter((day) => currentDays.some((current) => current.id === day.id))
    : seededDayIds
      ? (days as IDay[]).filter((day) => seededDayIds.has(day.id))
      : days as IDay[];
  const selectedTracks = seededTrackIds
    ? (tracks as ITrack[]).filter((track) => seededTrackIds.has(track.id))
    : tracks as ITrack[];
  const resolvedDays = tripChildren && currentDays === undefined
    ? undefined
    : daysReady ? selectedDays : undefined;
  const resolvedTracks = tracksReady ? selectedTracks : undefined;
  const value = useMemo(
    () => ({ trip: resolvedTrip, days: resolvedDays, tracks: resolvedTracks, details, setDetails, writers }),
    [resolvedTrip, resolvedDays, resolvedTracks, details, setDetails, writers],
  );
  return <TripDataContext.Provider value={value}>{children}</TripDataContext.Provider>;
}

// Store-backed TripView acquisition (#1612, tracker #665). Incoming props are
// optimistic seeds; normalized store snapshots own every subsequent live update.
export function TripDataView(props: TripDataProps & { children?: ReactNode }) {
  const store = useDataStore();
  const [details, setDetails] = useState<boolean>(
    props.initDetails !== undefined ? props.initDetails : props.focus == true,
  );

  // Seed before the selector subtree mounts, so init/current data has no empty first
  // frame and useDeps can suppress the corresponding REST request.
  const seed = nestedRows(props.trip, props.currentDays ?? [], props.initTracks ?? []);
  const seedRef = useRef(seed);
  seedRef.current = seed;
  const seedRevision = JSON.stringify(seed);

  useState(() => {
    if (!props.storeBacked) store.applyBundle({ entities: seed });
    return true;
  });

  useEffect(() => {
    if (!props.storeBacked) store.applyBundle({ entities: seedRef.current });
  }, [store, seedRevision, props.storeBacked]);

  return (
    <TripDataInner {...props} details={details} setDetails={setDetails}>
      {props.children}
    </TripDataInner>
  );
}
